import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, Eye, Trash2, Edit } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { turkeyData, provinces } from "@/data/turkeyData";

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ topic: "", category: "bursluluk", province: "", district: "", keywords: "" });
  const navigate = useNavigate();

  const load = () => api.get(`${V1}/blog?all=true&limit=100`).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const aiGenerate = async () => {
    if (!form.topic) return toast.error("Konu gerekli");
    setBusy(true);
    try {
      const { data } = await api.post(`${V1}/blog/ai/generate`, {
        topic: form.topic, category: form.category,
        province: form.province || null, district: form.district || null,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Taslak oluşturuldu");
      setOpen(false);
      navigate(`/admin/blog/duzenle/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Oluşturulamadı"); }
    finally { setBusy(false); }
  };

  const togglePublish = async (p) => {
    await api.patch(`${V1}/blog/${p.id}`, { published: !p.published });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`${V1}/blog/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-accent">İçerik Yönetimi</div>
          <h1 className="font-display text-3xl font-bold">Blog & SEO</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="new-blog-manual">
            <Link to="/admin/blog/yeni"><Plus className="h-4 w-4" /> Elle Ekle</Link>
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-blog-ai"><Sparkles className="h-4 w-4" /> AI ile Üret</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>AI ile Blog Yazısı Üret</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Konu</Label><Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="ör. Bursa bursluluk sınavları rehberi" data-testid="ai-topic" /></div>
                <div><Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="ai-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bursluluk">Bursluluk</SelectItem>
                      <SelectItem value="lgs">LGS</SelectItem>
                      <SelectItem value="tyt">TYT</SelectItem>
                      <SelectItem value="ayt">AYT</SelectItem>
                      <SelectItem value="il-rehberi">İl Rehberi</SelectItem>
                      <SelectItem value="genel">Genel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>İl</Label>
                    <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v, district: "" })}>
                      <SelectTrigger><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>İlçe</Label>
                    <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v })} disabled={!form.province}>
                      <SelectTrigger><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        {form.province && turkeyData[form.province] ? turkeyData[form.province].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        )) : null}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Anahtar Kelimeler (virgülle)</Label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="bursluluk, LGS, özel okul" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
                <Button onClick={aiGenerate} disabled={busy} data-testid="ai-generate-btn">
                  {busy ? "Üretiliyor…" : "Üret"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Başlık</th>
              <th className="text-left p-3">Kategori</th>
              <th className="text-left p-3">Görüntülenme</th>
              <th className="text-left p-3">Durum</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Henüz yazı yok.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`blog-row-${p.id}`}>
                <td className="p-3">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">/blog/{p.slug}</div>
                </td>
                <td className="p-3"><span className="px-2 py-0.5 bg-muted rounded text-xs uppercase">{p.category}</span></td>
                <td className="p-3 font-mono">{p.views}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(p)}
                    className={`px-2 py-0.5 rounded text-xs uppercase tracking-widest ${p.published ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}
                    data-testid={`toggle-publish-${p.id}`}
                  >
                    {p.published ? "Yayında" : "Taslak"}
                  </button>
                </td>
                <td className="p-3 flex gap-1 justify-end">
                  <Button asChild variant="ghost" size="sm"><Link to={`/blog/${p.slug}`} target="_blank"><Eye className="h-4 w-4" /></Link></Button>
                  <Button asChild variant="ghost" size="sm"><Link to={`/admin/blog/duzenle/${p.id}`}><Edit className="h-4 w-4" /></Link></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
